// src/AppRoutes.jsx — full replacement
import React from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext.jsx';

// Auth screens
import BootSpinner    from './auth/screens/BootSpinner.jsx';
import LoginScreen    from './auth/screens/LoginScreen.jsx';
import TenantPicker   from './auth/screens/TenantPicker.jsx';
import NoAccessScreen from './auth/screens/NoAccessScreen.jsx';
import ForgotPasswordScreen from './auth/screens/ForgotPasswordScreen.jsx';   // NEW
import PasswordResetScreen  from './auth/screens/PasswordResetScreen.jsx';    // NEW
import SetPasswordScreen    from './auth/screens/SetPasswordScreen.jsx';      // NEW
import AcceptInvitationScreen from './auth/screens/AcceptInvitationScreen.jsx';

// Shell + sections
import AppShell         from './shell/AppShell.jsx';
import Dashboard        from './sections/Dashboard.jsx';
import Credits          from './sections/Credits.jsx';
import ContentGenerator from './sections/ContentGenerator.jsx';
import SeoRewrite       from './sections/SeoRewrite.jsx';
import KnowledgeBase    from './sections/KnowledgeBase.jsx';
import Guida            from './sections/Guida.jsx';
import BillingDetails   from './sections/BillingDetails.jsx';
import Profile          from './sections/Profile.jsx';

// Billing (Stripe) — the Piani screen and the checkout return. Always mounted
// since 2026-09-24 (the build-time billing flag was deleted: the server answers 503
// STRIPE_NOT_CONFIGURED per request, more accurately than a build flag could).
import Piani            from './sections/Piani.jsx';
import CheckoutReturn   from './sections/CheckoutReturn.jsx';

// Admin
import AdminLayout           from './admin/AdminLayout.jsx';
import AdminOverviewPage     from './admin/pages/AdminOverviewPage.jsx';
import UserListPage          from './admin/pages/UserListPage.jsx';
import UserDetailPage        from './admin/pages/UserDetailPage.jsx';
import TenantManagementPage  from './admin/pages/TenantManagementPage.jsx';
import CompanyListPage       from './admin/pages/CompanyListPage.jsx';
import CompanyDetailPage     from './admin/pages/CompanyDetailPage.jsx';
import PlanListPage          from './admin/pages/PlanListPage.jsx';
import KnowledgeBasePage     from './admin/pages/KnowledgeBasePage.jsx';
import ContextSettingsPage   from './admin/pages/ContextSettingsPage.jsx';
import PersonasPage          from './admin/pages/PersonasPage.jsx';
import RunListPage           from './admin/pages/RunListPage.jsx';
import RunDetailPage         from './admin/pages/RunDetailPage.jsx';
import SystemSettingsLayout  from './admin/settings/SystemSettingsLayout.jsx';
import LegalDocumentsPage    from './admin/legal/LegalDocumentsPage.jsx';

// Tenant-admin — the "Gestione Workspace" console (tabs) plus the four
// single-workspace pages that keep their own paths.
import TenantAdminLayout     from './tenantAdmin/layout/TenantAdminLayout.jsx';
import ManageUsersPage       from './tenantAdmin/pages/ManageUsersPage.jsx';
import CompanyWorkspacesPage from './tenantAdmin/pages/CompanyWorkspacesPage.jsx';
import CompanyDetailsPage    from './tenantAdmin/pages/CompanyDetailsPage.jsx';
import CompanyCreditsPage    from './tenantAdmin/pages/CompanyCreditsPage.jsx';
import CompanyPlansPage      from './tenantAdmin/pages/CompanyPlansPage.jsx';
import ActiveCompanyGuard    from './tenantAdmin/components/ActiveCompanyGuard.jsx';
import TenantKnowledgeBasePage from './tenantAdmin/pages/TenantKnowledgeBasePage.jsx';
import BrandVoicePage        from './tenantAdmin/pages/BrandVoicePage.jsx';
import TenantPersonasPage    from './tenantAdmin/pages/TenantPersonasPage.jsx';
import TenantRunListPage     from './tenantAdmin/pages/TenantRunListPage.jsx';
import PersonalizationPage   from './onboarding/pages/PersonalizationPage.jsx';

function RequireSuperadmin({ children }) {
  const { isSuperadmin } = useAuth();
  if (!isSuperadmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireTenantAdmin({ children }) {
  const { isTenantAdmin, activeTenant, isSuperadmin, adminMode } = useAuth();
  // Superadmins always route through /admin/* — never the tenant-admin console.
  if (isSuperadmin) return <Navigate to={adminMode ? '/admin/users' : '/dashboard'} replace />;
  if (!isTenantAdmin || !activeTenant) return <Navigate to="/dashboard" replace />;
  return children;
}

// ⛔ NOT RequireTenantAdmin. That guard asks "is the ACTIVE workspace mine to
// administer?", which is the right question for Knowledge Base, personas,
// and runs — they act on the active workspace and nothing else.
// The "Gestione Workspace" console asks a different question: "does this user
// administer ANY workspace, or the company?" An admin of workspace B whose active
// workspace is A (where they are only an editor) belongs in the console; so does a
// company admin holding no workspace membership row of their own.
function RequireWorkspaceAdmin({ children }) {
  const { canManageWorkspaces, isSuperadmin, adminMode } = useAuth();
  if (isSuperadmin) return <Navigate to={adminMode ? '/admin/users' : '/dashboard'} replace />;
  if (!canManageWorkspaces) return <Navigate to="/dashboard" replace />;
  return children;
}

// The five company tabs. A non-company-admin lands on the Utenti tab rather than
// leaving the console: they got here legitimately, just not to this section.
function RequireCustomerAdmin({ children }) {
  const { isCustomerAdmin } = useAuth();
  if (!isCustomerAdmin) return <Navigate to="/tenant-admin/manage/users" replace />;
  return children;
}

// Which tab the console opens on. A company admin with no administered workspace
// has no Utenti tab at all, so sending them there would bounce them straight back.
function ManageIndex() {
  const { adminTenants } = useAuth();
  // Defensive: test mocks (and any consumer) may omit it.
  return <Navigate to={(adminTenants || []).length ? 'users' : 'workspaces'} replace />;
}

// ⛔ The recovery funnel must survive an UNHEALTHY auth service — that is the
// situation it exists for. These three routes are unauthenticated by
// construction, so they need nothing from the boot sequence.
// ⚠️ Keep in sync with the <Route path> values below, and with the BACKEND
// config that names two of them (password_reset_url / set_password_url).
const PUBLIC_RECOVERY_PATHS = ['/forgot-password', '/password-reset', '/set-password'];

export default function AppRoutes() {
  const { phase } = useAuth();
  const { pathname } = useLocation();

  // `bootSequence` awaits GET /auth/me with NO timeout whenever a token is in
  // localStorage. Gating every route on it meant a user clicking an emailed
  // reset link waited out that round trip on a spinner — and, if the fetch
  // hung rather than failed, never reached the form at all.
  if (phase === 'booting' && !PUBLIC_RECOVERY_PATHS.includes(pathname)) {
    return <BootSpinner />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login"         element={<LoginScreen />} />
      <Route path="/select-tenant" element={<TenantPicker />} />
      <Route path="/no-access"     element={<NoAccessScreen />} />

      {/* Public recovery funnel — issue #34. Outside AppShell: the caller has no
          token by construction.
          ⚠️ /password-reset and /set-password are named by BACKEND config
          (password_reset_url / set_password_url in
          context-library/product/transactional-email.yaml). Renaming either
          here breaks every mail already in flight. */}
      <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
      <Route path="/password-reset"  element={<PasswordResetScreen />} />
      <Route path="/set-password"    element={<SetPasswordScreen />} />

      {/* Invitation accept — top-level, OUTSIDE AppShell: the shell redirects a
          signed-out caller and remembers nothing of the URL, which would drop
          the token on the floor. The screen parks the token itself and sends the
          visitor through /login.
          ⛔ Deliberately NOT in PUBLIC_RECOVERY_PATHS: unlike the recovery
          funnel this route NEEDS the session (the accept endpoint requires a
          bearer for the invited account), so waiting out the boot sequence is
          correct here — `phase` is what tells the screen whether to accept or to
          ask for a sign-in.
          ⚠️ The path is named by BACKEND config (`invitation_url`, a complete
          absolute URL): renaming it 404s every invitation mail in flight. */}
      <Route path="/invitations/accept" element={<AcceptInvitationScreen />} />

      {/* Protected routes — AppShell redirects if not authenticated */}
      <Route element={<AppShell />}>
        <Route index                     element={<Navigate to="/content-generator" replace />} />
        <Route path="/dashboard"         element={<Dashboard />} />
        <Route path="/credits"           element={<Credits />} />
        <Route path="/content-generator" element={<ContentGenerator />} />
        <Route path="/seo-rewrite"       element={<SeoRewrite />} />
        <Route path="/knowledge-base"    element={<KnowledgeBase />} />
        <Route path="/guida"             element={<Guida />} />
        <Route path="/billing-details"   element={<BillingDetails />} />
        <Route path="/profile"           element={<Profile />} />

        {/* Stripe screens — unconditional. /billing is the Piani screen
            (customer-admin only; everyone else sees a "riservata" panel). */}
        <Route path="/billing"           element={<Piani />} />
        <Route path="/billing/return"    element={<CheckoutReturn />} />

        {/* Run history — available to every tenant member (no admin guard).
            BE /pipeline/runs is tenant-scoped (not admin-only) and nulls creator
            PII for non-admins, so members see the full tenant set safely. */}
        <Route path="/runs"          element={<TenantRunListPage />} />
        <Route path="/runs/:taskId"  element={<RunDetailPage />} />

        {/* Admin routes — defense-in-depth gate; BE 403 still backs this */}
        <Route path="/admin" element={<RequireSuperadmin><AdminLayout /></RequireSuperadmin>}>
          <Route index element={<Navigate to="/admin/overview" replace />} />
          <Route path="overview" element={<AdminOverviewPage />} />
          <Route path="users" element={<UserListPage />} />
          <Route path="users/:userId" element={<UserDetailPage />} />
          <Route path="tenants" element={<TenantManagementPage />} />
          <Route path="companies" element={<CompanyListPage />} />
          <Route path="companies/:customerId" element={<CompanyDetailPage />} />
          <Route path="plans" element={<PlanListPage />} />
          <Route path="knowledge-base" element={<KnowledgeBasePage />} />
          <Route path="context-settings" element={<ContextSettingsPage />} />
          <Route path="personas" element={<PersonasPage />} />
          <Route path="runs" element={<RunListPage />} />
          <Route path="runs/:taskId" element={<RunDetailPage />} />
          {/* «Configurazione» = SYSTEM settings (v219). AdminLayout hides its old tab row here. The tenant
              configuration editor stays at /admin/context-settings («Configurazione tenant»). */}
          <Route path="settings" element={<SystemSettingsLayout />}>
            <Route index element={<Navigate to="/admin/settings/legal-documents" replace />} />
            <Route path="legal-documents" element={<LegalDocumentsPage />} />
          </Route>
        </Route>

        {/* Tenant-admin routes — TWO guards, deliberately.
            The /tenant-admin segment itself is UNGUARDED so its two redirects can
            run for anyone: /tenant-admin/users is the path every old link, bookmark
            and email points at, and it must reach the new console (which then
            applies its own guard) rather than 404 or bounce on a guard that asks
            the wrong question. */}
        <Route path="/tenant-admin" element={<Outlet />}>
          <Route index element={<Navigate to="/tenant-admin/manage" replace />} />
          <Route path="users" element={<Navigate to="/tenant-admin/manage/users" replace />} />

          {/* "Gestione Workspace" — the console, its tabs and the company tabs. */}
          <Route path="manage" element={<RequireWorkspaceAdmin><TenantAdminLayout /></RequireWorkspaceAdmin>}>
            <Route index element={<ManageIndex />} />
            <Route path="users" element={<ManageUsersPage />} />
            <Route path="workspaces" element={<RequireCustomerAdmin><CompanyWorkspacesPage /></RequireCustomerAdmin>} />
            <Route path="company" element={<RequireCustomerAdmin><CompanyDetailsPage /></RequireCustomerAdmin>} />
            <Route path="credits" element={<RequireCustomerAdmin><ActiveCompanyGuard><CompanyCreditsPage /></ActiveCompanyGuard></RequireCustomerAdmin>} />
            <Route path="plans" element={<RequireCustomerAdmin><ActiveCompanyGuard><CompanyPlansPage /></ActiveCompanyGuard></RequireCustomerAdmin>} />
          </Route>

          {/* The single-workspace pages: PATHS UNCHANGED, guard unchanged. They act
              on the ACTIVE workspace only, so RequireTenantAdmin (not
              RequireWorkspaceAdmin) is still exactly right — it is applied through a
              pathless route so the four entries share one guard instance. */}
          <Route element={<RequireTenantAdmin><Outlet /></RequireTenantAdmin>}>
            <Route path="knowledge-base" element={<TenantKnowledgeBasePage />} />
            {/* "Brand Voice" — the brand-identity editor (v183: the shell). Brian
                Cortex sits it between Knowledge Base and Buyer Personas. */}
            <Route path="brand-voice" element={<BrandVoicePage />} />
            {/* ⛔ No /tenant-admin/context-settings (removed 2026-09-27): editing the
                workspace's configuration files directly is no longer allowed to
                workspace admins. The URL falls through to the catch-all → "/". The
                superadmin editor stays at /admin/context-settings. */}
            <Route path="personas" element={<TenantPersonasPage />} />
            {/* The first-login personalization wizard (one form → progress).
                ACTIVE workspace only, like its siblings: every route it calls is TenantAdmin-gated.
                ⚠️ The path is duplicated as PERSONALIZATION_PATH in src/onboarding/onboardingModel.js. */}
            <Route path="personalization" element={<PersonalizationPage />} />
            <Route path="runs" element={<TenantRunListPage />} />
            <Route path="runs/:taskId" element={<RunDetailPage />} />
          </Route>
        </Route>
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
